import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { COACHES, OFFERINGS } from "@/lib/mockData";
import { useState } from "react";
import { Calendar as CalIcon, Clock, Check } from "lucide-react";

const SLOTS = ["09:00","10:00","11:00","14:00","15:00","16:00","18:00","19:00","20:00","21:00"];

export default function Booking() {
  const { username } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const coach = COACHES.find(c => c.username === username);
  const [offerId, setOfferId] = useState(OFFERINGS.some(o => o.id === params.get("offer")) ? params.get("offer") : (OFFERINGS[0]?.id || null));
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const [slot, setSlot] = useState("16:00");
  const offer = OFFERINGS.find(o => o.id === offerId);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (!coach) return <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10"><h1 className="page-heading" data-testid="booking-empty">Coach introuvable.</h1><p className="text-slate-400 mt-2">Aucun coach disponible pour le moment.</p></div>;

  const confirm = () => {
    if (!date || date < new Date().toISOString().slice(0,10)) return;
    nav("/checkout", { state: { coach, offer, date, slot } });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 data-testid="booking-title" className="font-display text-3xl font-bold text-white mb-2">Une séance avec {coach.name.split("'")[1]}.</h1>
      <p className="text-slate-400 mb-8">Votre séance, à votre rythme. Fuseau horaire : <span className="text-blue-400 font-mono">{tz}</span></p>
      
      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-6">
          <div className="glass rounded-2xl p-6">
            <h2 className="font-display font-bold text-white mb-4">Select offering</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {OFFERINGS.filter(o => o.type === "Live" || o.type === "Async").map(o => (
                <button key={o.id} data-testid={`booking-offer-${o.id}`} onClick={() => setOfferId(o.id)}
                  className={`text-left p-4 rounded-xl border transition ${offerId === o.id ? "border-blue-500 bg-blue-500/10" : "border-white/10 bg-white/5 hover:border-white/20"}`}>
                  <div className="flex justify-between mb-1">
                    <div className="text-xs font-mono uppercase text-slate-400">{o.type}</div>
                    <div className="font-bold text-white">€{o.price}</div>
                  </div>
                  <div className="font-semibold text-white text-sm">{o.title}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="glass rounded-2xl p-6">
            <h2 className="font-display font-bold text-white mb-4 flex items-center gap-2"><CalIcon className="w-4 h-4 text-blue-400" /> Pick a date</h2>
            <input data-testid="booking-date" aria-label="Date de la séance" type="date" min={new Date().toISOString().slice(0,10)} required value={date} onChange={e => setDate(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500/50" />
          </div>

          <div className="glass rounded-2xl p-6">
            <h2 className="font-display font-bold text-white mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-blue-400" /> Select a time slot</h2>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {SLOTS.map((s, i) => {
                const busy = i % 4 === 2;
                const almost = i % 5 === 0;
                return (
                  <button key={s} data-testid={`slot-${s}`} disabled={busy} onClick={() => setSlot(s)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${
                      busy ? "border-white/5 bg-white/5 text-slate-600 cursor-not-allowed line-through" :
                      slot === s ? "border-blue-500 bg-blue-500/20 text-white" :
                      almost ? "border-amber-500/40 bg-amber-500/5 text-amber-300 hover:border-amber-500" :
                      "border-white/10 bg-white/5 text-slate-200 hover:border-blue-500/50"
                    }`}>{s}</button>
                );
              })}
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-400">
              <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Disponible</div>
              <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /> Almost full</div>
              <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-600" /> Busy</div>
            </div>
          </div>
        </div>

        <aside className="glass rounded-2xl p-6 h-fit lg:sticky lg:top-24">
          <h3 className="font-display font-bold text-white mb-4">Récapitulatif de la séance</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Coach</span><span className="text-white font-medium">{coach.name.split("'")[1] || coach.name}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Offering</span><span className="text-white font-medium truncate max-w-[180px]">{offer?.title}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Date</span><span className="text-white font-medium">{date}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Time</span><span className="text-white font-medium">{slot}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Duration</span><span className="text-white font-medium">{offer?.duration || "—"} min</span></div>
            <div className="border-t border-white/10 pt-3 flex justify-between text-lg">
              <span className="text-slate-300">Total</span><span className="font-display font-extrabold text-white">€{offer?.price}</span>
            </div>
          </div>
          <button data-testid="booking-confirm" disabled={!date || date < new Date().toISOString().slice(0,10)} onClick={confirm} className="mt-5 w-full btn-primary text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
            <Check className="w-4 h-4" /> Continuer
          </button>
        </aside>
      </div>
    </div>
  );
}
